import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { KCN_STATS, type KcnStat } from "@/lib/stats";

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function CountUp({ item }: { item: KcnStat }) {
  const ref = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(item.target);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const begin = () => {
      if (started.current) return;
      started.current = true;
      const duration = 1800;
      const start = performance.now();
      setValue(0);
      const tick = (now: number) => {
        const p = Math.min((now - start) / duration, 1);
        setValue(item.target * easeOutCubic(p));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    // Already in viewport on mount (back-nav with restored scroll) → start now.
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    if (r.top < vh && r.bottom > 0) {
      begin();
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) begin();
        });
      },
      { threshold: 0.4 },
    );
    obs.observe(el);

    // Safety net: if the observer never fires, snap to final value.
    const fallback = window.setTimeout(() => {
      if (!started.current) {
        started.current = true;
        setValue(item.target);
      }
    }, 1200);

    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted && !started.current) {
        started.current = true;
        setValue(item.target);
      }
    };
    window.addEventListener("pageshow", onPageShow);

    return () => {
      obs.disconnect();
      window.clearTimeout(fallback);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [item.target]);

  return (
    <div ref={ref} className="home-stat-number">
      {item.prefix}
      {Math.floor(value).toString()}
      {item.suffix}
    </div>
  );
}

/**
 * Shared KCN metrics band. Numbers come from src/lib/stats.ts - edit them there.
 * variant="animated": count-up treatment (home page)
 * variant="static": plain typographic treatment
 */
export function StatsBand({
  variant = "animated",
  className,
}: {
  variant?: "animated" | "static";
  className?: string;
}) {
  if (variant === "static") {
    return (
      <div className={cn("grid grid-cols-2 md:grid-cols-4 gap-y-16", className)}>
        {KCN_STATS.map((s, i) => (
          <div
            key={s.key}
            className={cn(
              "text-center px-4",
              i !== KCN_STATS.length - 1 && "md:border-r border-border/40",
            )}
          >
            <div className="headline-stat text-foreground">{s.value}</div>
            <div className="mt-5 text-xs uppercase tracking-[0.24em] text-foreground/60">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("home-stats-grid", className)}>
      {KCN_STATS.map((s) => (
        <div key={s.key} className="home-stat-cell">
          <CountUp item={s} />
          <div className="home-stat-label">{s.label}</div>
        </div>
      ))}
    </div>
  );
}
